/**
 * Charged damage lines at a chosen number of charges — or of casts, for a
 * spell that hits harder each time it is cast in a turn (Pugilat).
 *
 * The spell export keeps two rows per charged hit (backend
 * `dofus_api/spell_effects.extract_full_effect_lines`): the base row
 * (`stack_index` 0) and the peak row (`stack_index` == `stack_max`, the dice
 * plus `stack_max` times the per-charge bonus). Every tier in between is the
 * same straight line, so a chosen count is read off those two rows rather than
 * exported.
 *
 * The peak row keeps its identity — its line key, which is what a criterion
 * and its weight are stored under — and only its numbers move. That is why a
 * count is stored separately (`Build.spellCharges`) and why the server repeats
 * the same arithmetic (`gen/engine/spell_scoring.charged_row_at`) on the same
 * two rows: MUST stay in lockstep with it.
 */

/**
 * The scalings whose count the player picks, and how a count maps to a tier:
 * `tier = count - offset`. A charge count is its tier (5 charges = tier 5); a
 * cast count is one more than its tier, since the first cast is the base hit
 * (Pugilat's tier 3 is "4 lancers"). Enemies in an area are not a pick.
 */
export const CHARGE_SCALING_KIND = 'charge';
export const CAST_REPEAT_SCALING_KIND = 'cast_repeat';
const COUNT_OFFSET_BY_KIND = { [CHARGE_SCALING_KIND]: 0, [CAST_REPEAT_SCALING_KIND]: 1 };

const CAPTION_BY_KIND = {
    [CHARGE_SCALING_KIND]: {
        fr: ['{n} charge', '{n} charges'],
        en: ['{n} charge', '{n} charges'],
        de: ['{n} Aufladung', '{n} Aufladungen'],
        es: ['{n} carga', '{n} cargas'],
        pt: ['{n} carga', '{n} cargas'],
    },
    [CAST_REPEAT_SCALING_KIND]: {
        fr: ['{n} lancer', '{n} lancers'],
        en: ['{n} cast', '{n} casts'],
        de: ['{n} Wurf', '{n} Würfe'],
        es: ['{n} lanzamiento', '{n} lanzamientos'],
        pt: ['{n} lançamento', '{n} lançamentos'],
    },
};

/** True for a scaling whose count the player picks. */
export const isPickableScaling = (kind) => Object.prototype.hasOwnProperty.call(COUNT_OFFSET_BY_KIND, kind);

const countOffset = (kind) => COUNT_OFFSET_BY_KIND[kind] ?? 0;

/** The largest count a peak row stands for: its tier plus the kind's offset. */
export const maxCount = (kind, stackMax) => Number(stackMax) + countOffset(kind);

const toInt = (v) => {
    const n = Number(v);
    return Number.isFinite(n) ? Math.trunc(n) : null;
};

/** "3 charges" / "4 lancers", in each language, the way the export captions the peak row. */
export const chargesCaption = (n, kind = CHARGE_SCALING_KIND) => Object.fromEntries(
    Object.entries(CAPTION_BY_KIND[kind] || CAPTION_BY_KIND[CHARGE_SCALING_KIND]).map(([lang, [one, many]]) => [
        lang,
        (n === 1 ? one : many).replace('{n}', String(n)),
    ]),
);

/** One language's caption, for a control that shows a single string. */
export const chargesLabel = (n, lang, kind = CHARGE_SCALING_KIND) => {
    const caption = chargesCaption(n, kind);
    return caption[lang] || caption.en;
};

const isEmbedded = (row) => (row?.embedded_from_spell_id ?? '') !== ''
    || (row?.embedding_carrier_effect_id ?? '') !== '';

/**
 * A peak row whose charge count the player can pick. Embedded rows are left
 * out: their numbers are part of their line key, so moving them would lose the
 * criterion they belong to.
 */
export const isChargeablePeakRow = (row) => {
    const index = toInt(row?.stack_index);
    const max = toInt(row?.stack_max);
    return row?.type === 'damage'
        && isPickableScaling(row?.scaling_kind)
        && max != null && max >= 1
        && maxCount(row.scaling_kind, max) >= 2
        && index === max
        && !isEmbedded(row);
};

/** `base + (peak - base) * n / max`, floored: the same on the server. */
const atCharges = (base, peak, n, max) => {
    const b = toInt(base);
    const p = toInt(peak);
    if (b == null || p == null) return peak;
    return b + Math.floor(((p - b) * n) / max);
};

/** The peak row re-read at a count of `n` (charges or casts), given its base row. */
export function chargedRowAt(peak, base, n) {
    const max = toInt(peak?.stack_max);
    const kind = peak?.scaling_kind;
    const tier = n - countOffset(kind);
    if (!base || max == null || tier >= max) return peak;
    return {
        ...peak,
        min: atCharges(base.min, peak.min, tier, max),
        max: atCharges(base.max, peak.max, tier, max),
        crit_min: atCharges(base.crit_min, peak.crit_min, tier, max),
        crit_max: atCharges(base.crit_max, peak.crit_max, tier, max),
        row_caption: chargesCaption(n, kind),
    };
}

/** A stored count reduced to 1..max, or null when it is the default (max). */
export const clampCharges = (n, max) => {
    const count = toInt(n);
    if (count == null || max == null) return null;
    const clamped = Math.min(max, Math.max(1, count));
    return clamped >= max ? null : clamped;
};

/**
 * Spells with every chargeable peak row moved to its chosen count.
 *
 * `chargesBySpell` is `{ spellId: { lineKey: n } }`; `keyOf` is the raw line
 * key (`spellUtils.rawEffectLineKey`), passed in so this module stays free of
 * spellUtils. Spells and levels without a chosen count come back as they were.
 */
export function applySpellCharges(spells, chargesBySpell, keyOf) {
    if (!Array.isArray(spells) || !chargesBySpell || typeof keyOf !== 'function') return spells;
    return spells.map((spell) => {
        const counts = chargesBySpell[spell?.id];
        if (!counts || !Object.keys(counts).length || !Array.isArray(spell?.levels)) return spell;
        const levels = spell.levels.map((level) => {
            const lines = level?.effect_lines;
            if (!Array.isArray(lines)) return level;
            let changed = false;
            const next = lines.map((row) => {
                if (!isChargeablePeakRow(row)) return row;
                const n = clampCharges(counts[keyOf(row)], maxCount(row.scaling_kind, toInt(row.stack_max)));
                if (n == null) return row;
                const baseKey = keyOf({ ...row, stack_index: 0 });
                const base = lines.find((other) => toInt(other?.stack_index) === 0 && keyOf(other) === baseKey);
                if (!base) return row;
                changed = true;
                return chargedRowAt(row, base, n);
            });
            return changed ? { ...level, effect_lines: next } : level;
        });
        return { ...spell, levels };
    });
}
